import React from "react";
import { money, computePaystub } from "../lib/paystub";

const Row = ({ cols }) => (
  <div className="grid grid-cols-12 gap-1 py-[2px] text-[10px] leading-tight">
    {cols.map((c, i) => (
      <div key={i} className={`${c.span} ${c.right ? "text-right font-mono-num" : ""} ${c.bold ? "font-bold" : ""} break-words`}>
        {c.v}
      </div>
    ))}
  </div>
);

export const PaystubPreview = ({ form }) => {
  const c = computePaystub(form);
  const name = `${form.firstName} ${form.lastName}`.trim().toUpperCase() || "EMPLOYEE NAME";
  const company = (form.companyName || "COMPANY NAME").toUpperCase();
  const isDD = form.paymentMethod === "direct_deposit";

  return (
    <div
      className="print-sheet bg-white text-[#0f172a] w-full p-5 sm:p-7"
      style={{ fontFamily: "Inter, sans-serif", minHeight: "60vh" }}
    >
      {/* Header */}
      <div className="flex justify-between items-start gap-4">
        <div className="min-w-0">
          <div className="text-sm font-bold break-words">{name}</div>
          <div className="text-[9px] font-bold mt-1 break-words">(BRN)&nbsp;&nbsp;{company}</div>
          <div className="text-[9px] text-[#334155]">SSN:&nbsp; xxx-xx-{form.ssnLast4 || "0000"}</div>
          <div className="text-[9px] text-[#334155]">Pay Period: {form.payPeriodStart || "—"} to {form.payPeriodEnd || "—"}</div>
          <div className="text-[9px] text-[#334155]">Paycheck Date: {form.payDate || "—"}</div>
          <div className="text-[9px] text-[#334155]">Paycheck Number: {form.employerNumber || "—"}</div>
          {form.department ? <div className="text-[9px] text-[#334155]">Department: {form.department}</div> : null}
        </div>
        <div className="text-right shrink-0">
          <div className="text-sm font-bold font-display whitespace-nowrap">Earnings Statement</div>
          <div className="text-[9px] mt-1">Federal: {form.filingStatus} / {form.federalAllowances} / $0.00</div>
          <div className="text-[9px]">State: {form.filingStatus} / 0 / 0 / $0.00</div>
        </div>
      </div>

      {/* Earnings */}
      <section className="mt-6">
        <Row cols={[
          { v: "Earnings", span: "col-span-4", bold: true },
          { v: "Pay Rate", span: "col-span-2", right: true, bold: true },
          { v: "Hours", span: "col-span-2", right: true, bold: true },
          { v: "Current", span: "col-span-2", right: true, bold: true },
          { v: "YTD", span: "col-span-2", right: true, bold: true },
        ]} />
        <div className="border-b border-slate-800" />
        {c.earnings.map((e, i) => (
          <Row key={i} cols={[
            { v: e.description, span: "col-span-4" },
            { v: e.payRate ? e.payRate.toFixed(2) : "", span: "col-span-2", right: true },
            { v: e.hours ? e.hours.toFixed(2) : "", span: "col-span-2", right: true },
            { v: money(e.current), span: "col-span-2", right: true },
            { v: money(e.ytd), span: "col-span-2", right: true },
          ]} />
        ))}
        <div className="border-t border-slate-400 mt-1" />
        <Row cols={[
          { v: "Gross Earnings", span: "col-span-4", bold: true },
          { v: "", span: "col-span-2" },
          { v: c.totalHours.toFixed(2), span: "col-span-2", right: true, bold: true },
          { v: money(c.grossCurrent), span: "col-span-2", right: true, bold: true },
          { v: money(c.grossYTD), span: "col-span-2", right: true, bold: true },
        ]} />
      </section>

      {/* Taxes */}
      <section className="mt-6">
        <Row cols={[
          { v: "Taxes", span: "col-span-6", bold: true },
          { v: "Current", span: "col-span-3", right: true, bold: true },
          { v: "YTD", span: "col-span-3", right: true, bold: true },
        ]} />
        <div className="border-b border-slate-300 mb-[2px]" />
        {c.taxes.map((t, i) => (
          <Row key={i} cols={[
            { v: t.description, span: "col-span-6" },
            { v: money(t.current), span: "col-span-3", right: true },
            { v: money(t.ytd), span: "col-span-3", right: true },
          ]} />
        ))}
      </section>

      {/* Deductions */}
      <section className="mt-6">
        <Row cols={[
          { v: "Deductions", span: "col-span-6", bold: true },
          { v: "Current", span: "col-span-3", right: true, bold: true },
          { v: "YTD", span: "col-span-3", right: true, bold: true },
        ]} />
        <div className="border-b border-slate-300 mb-[2px]" />
        {c.deductions.map((d, i) => (
          <Row key={i} cols={[
            { v: d.description, span: "col-span-6" },
            { v: money(d.current), span: "col-span-3", right: true },
            { v: money(d.ytd), span: "col-span-3", right: true },
          ]} />
        ))}
        <div className="border-t border-slate-400 mt-1" />
        <Row cols={[
          { v: "Net Earnings", span: "col-span-6", bold: true },
          { v: money(c.netCurrent), span: "col-span-3", right: true, bold: true },
          { v: money(c.netYTD), span: "col-span-3", right: true, bold: true },
        ]} />
      </section>

      {isDD && (
        <section className="mt-6">
          <div className="text-[10px] font-bold">Direct Deposit Distribution</div>
          <div className="border-b border-slate-300 mb-[2px]" />
          <Row cols={[
            { v: `Checking ${form.bankName || ""}`.trim(), span: "col-span-6" },
            { v: money(c.netCurrent), span: "col-span-3", right: true },
            { v: money(c.netYTD), span: "col-span-3", right: true },
          ]} />
        </section>
      )}

      {/* Check / advice stub — its own block, normal flow */}
      <section className="mt-8 pt-4 border-t border-slate-300">
        <div className="flex justify-between items-start gap-4">
          <div className="min-w-0">
            <div className="text-[10px] font-bold break-words">{company}</div>
            <div className="text-[8px] text-[#475569] break-words">{form.companyAddress || "—"}</div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-[11px] font-bold">{form.employerNumber || "—"}</div>
            <div className="text-[9px] font-bold">Date: {form.payDate || "—"}</div>
          </div>
        </div>

        {form.bankName ? (
          <div className="text-[9px] mt-2 break-words">Bank: {form.bankName}</div>
        ) : null}

        <div className="flex items-center gap-2 mt-4">
          <span className="text-[9px] font-bold">PAY</span>
          <div className="flex-1 border border-slate-300 rounded px-2 py-[3px] flex justify-between gap-2 min-w-0">
            <span className="text-[8px] text-[#475569] truncate">To the order of {name}</span>
            <span className="text-[9px] font-mono-num font-bold shrink-0">{money(c.netCurrent)}</span>
          </div>
        </div>
        {isDD && (
          <div className="text-center text-[9px] font-bold mt-3">***** NON-NEGOTIABLE ***** Not a check — direct deposit advice.</div>
        )}
      </section>
    </div>
  );
};
